import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PencilLine from '@/assets/icons/pencil_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TextField from '@/components/atoms/TextField';
import { confirm } from '@/components/molecules/ConfirmDialog';

import styles from './Client.module.scss';

interface HeadingClient {
    id: number;
    name: string;
    archived?: boolean;
}

type UpdateClient = (
    clientId: number,
    data: { name?: string; archived?: boolean },
    options?: { reportError?: boolean }
) => Promise<void>;

export interface ClientHeading {
    title: string;
    subtitle?: string;
    titleAction?: ReactNode;
    titleControl?: ReactNode;
    actions?: ReactNode;
}

/**
 * Archive, restore and rename for the client page header. Rename edits the title in
 * place; archive asks first, because it moves the client to Inactive clients.
 */
export function useClientHeading(
    client: HeadingClient,
    onUpdateClient?: UpdateClient
): ClientHeading {
    const { t } = useTranslation();
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(client.name);
    const [nameLocked, setNameLocked] = useState(false);
    // The name just saved, shown until the refetched client carries it. Otherwise the
    // heading snaps back to the previous name for as long as that refetch takes.
    const shownName = useRef<string | null>(null);
    // Set while a rename is closing, so the field's blur does not save a second time.
    const dismiss = useRef(false);
    const saving = useRef(false);
    // Bumped when the client changes, so a rename started for the previous client
    // cannot write its name onto this one.
    const renameFor = useRef(0);
    const renameButtonRef = useRef<HTMLButtonElement>(null);
    const returnFocus = useRef(false);
    const [trackedClientId, setTrackedClientId] = useState(client.id);
    if (trackedClientId !== client.id) {
        setTrackedClientId(client.id);
        setRenaming(false);
        setDraft(client.name);
        setNameLocked(false);
        shownName.current = null;
        dismiss.current = true;
        saving.current = false;
        renameFor.current += 1;
    }
    const archived = client.archived === true;
    if (shownName.current === client.name) {
        shownName.current = null;
    }
    const displayedName = shownName.current ?? client.name;

    useEffect(() => {
        if (!renaming && returnFocus.current) {
            returnFocus.current = false;
            renameButtonRef.current?.focus();
        }
    }, [renaming]);

    function closeRename(): void {
        dismiss.current = true;
        returnFocus.current = true;
        setRenaming(false);
    }

    function startRename(): void {
        dismiss.current = false;
        setDraft(displayedName);
        setRenaming(true);
    }

    function finishRename(save: boolean): void {
        if (dismiss.current || saving.current) return;
        const next = draft.trim();
        if (!save || !next || next === displayedName || !onUpdateClient) {
            closeRename();
            return;
        }
        saving.current = true;
        setNameLocked(true);
        dismiss.current = true;
        const generation = renameFor.current;
        void onUpdateClient(client.id, { name: next })
            .then(() => {
                if (generation !== renameFor.current) return;
                shownName.current = next;
                returnFocus.current = true;
                setRenaming(false);
            })
            .catch(() => {
                if (generation !== renameFor.current) return;
                // The page toasts the failure; the field stays open with the typed name.
                dismiss.current = false;
            })
            .finally(() => {
                if (generation !== renameFor.current) return;
                saving.current = false;
                setNameLocked(false);
            });
    }

    function onNameKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            finishRename(true);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            finishRename(false);
        }
    }

    function archiveClient(): void {
        if (!onUpdateClient) return;
        const nextArchived = !archived;
        void confirm({
            title: nextArchived ? t('internal.client.archive') : t('internal.client.restore'),
            message: nextArchived
                ? t('internal.client.confirmArchive', { name: client.name })
                : t('internal.client.confirmRestore', { name: client.name }),
            confirmLabel: nextArchived
                ? t('internal.client.archive')
                : t('internal.client.restore'),
            confirmTone: 'primary',
            onConfirm: () =>
                onUpdateClient(client.id, { archived: nextArchived }, { reportError: false }),
        });
    }

    return {
        title: displayedName,
        subtitle: archived ? t('internal.client.statusArchived') : undefined,
        titleAction:
            onUpdateClient && !renaming ? (
                <Button
                    variant="ghost"
                    size="medium"
                    iconOnly
                    aria-label={t('internal.client.rename')}
                    tooltip={t('internal.client.renameHint')}
                    icon={<SvgIcon icon={PencilLine} color="currentColor" />}
                    ref={renameButtonRef}
                    onClick={startRename}
                />
            ) : undefined,
        titleControl: renaming ? (
            <>
                <h1 className={styles.visuallyHidden}>{displayedName}</h1>
                <TextField
                    className={styles.nameField}
                    aria-label={t('internal.client.clientName')}
                    value={draft}
                    autoFocus
                    readOnly={nameLocked}
                    onChange={(event) => {
                        if (!saving.current) setDraft(event.target.value);
                    }}
                    onKeyDown={onNameKeyDown}
                    onBlur={() => finishRename(true)}
                />
            </>
        ) : undefined,
        actions: onUpdateClient ? (
            <Button
                variant="primary"
                size="medium"
                tooltip={
                    archived ? t('internal.client.restoreHint') : t('internal.client.archiveHint')
                }
                onClick={archiveClient}>
                {archived ? t('internal.client.restore') : t('internal.client.archive')}
            </Button>
        ) : undefined,
    };
}
